import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Cinematic hero video player.
 *
 * Behaviour:
 * - The video starts playing when it enters the viewport (via IntersectionObserver).
 * - It ALWAYS starts muted, so it never disturbs the visitor when the page opens.
 * - Audio is only enabled if the user explicitly clicks the round toggle in the
 *   top-right corner — no automatic unmute on scroll or hover.
 * - Once playing, the video loops silently until the user decides to enable audio.
 */
export default function CinematicVideo({ src, poster, testId = "cinematic-video", ariaLabel }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const startedRef = useRef(false);
  const tooltipShownRef = useRef(false);

  // Start playback (muted) the first time the video enters the viewport.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const startPlayback = async () => {
      if (startedRef.current) return;
      startedRef.current = true;
      v.muted = true;
      try { await v.play(); } catch { /* autoplay blocked — user can press the toggle */ }
      // Show the audio-available tooltip once, right after playback starts.
      if (!tooltipShownRef.current) {
        tooltipShownRef.current = true;
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 4500);
      }
    };

    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) startPlayback();
      }
    }, { threshold: [0, 0.35, 0.6, 1.0] });

    io.observe(v);
    return () => io.disconnect();
  }, [src]);

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    setShowTooltip(false); // Dismiss tooltip on interaction
    if (!v.muted) {
      v.volume = 1.0;
      v.play().catch(() => {});
    }
  };

  return (
    <div className="relative bg-black overflow-hidden select-none" data-testid={`${testId}-wrapper`}>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        loop
        muted
        playsInline
        preload="auto"
        aria-label={ariaLabel || "Video del cantiere"}
        data-testid={testId}
        className="w-full h-auto block"
      >
        Il tuo browser non supporta il tag video.
      </video>

      {/* Tooltip fluttuante "Audio disponibile" — appare 4.5s alla prima visualizzazione */}
      {showTooltip && muted && (
        <div
          data-testid={`${testId}-tooltip`}
          className="absolute top-3 right-16 md:top-4 md:right-20 z-10 pointer-events-none animate-tooltip-in"
        >
          <div className="relative bg-white text-black px-4 py-2.5 rounded-lg shadow-xl font-mono text-xs uppercase tracking-widest whitespace-nowrap">
            Audio disponibile — click
            {/* Arrow pointing to the mute button on the right */}
            <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 bg-white rotate-45"></span>
          </div>
        </div>
      )}

      {/* Mute/unmute toggle — arancione pulsante quando muted per invitare l'utente ad attivare l'audio */}
      <button
        type="button"
        onClick={toggleMute}
        data-testid={`${testId}-mute-toggle`}
        aria-label={muted ? "Attiva audio" : "Disattiva audio"}
        title={muted ? "Attiva audio" : "Disattiva audio"}
        className={`absolute top-3 right-3 md:top-4 md:right-4 z-10 inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full backdrop-blur-sm text-white transition-all ${
          muted
            ? "bg-primary hover:bg-primary/90 shadow-lg animate-pulse-slow"
            : "bg-black/50 hover:bg-black/70 border border-white/20"
        }`}
      >
        {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
      </button>
    </div>
  );
}
